/** @jsx jsx */

import React, { Fragment, useState } from 'react'

import { Box, Heading, jsx, Grid } from 'theme-ui'
import { useWindowSize } from '../../hooks/useWindowSize'
import ProductCard, { FeaturedProduct } from '../product/ProductCard'
import InfoCard from '../product/InfoCard'
import { useCheckProductsInStock } from '../../hooks/queries'
import CarouselSlider from '../shared/Carousel/CarouselSlider'

function getAvailableProductsWithImages(products, { data, loading, error }) {
  const noLiveData = loading || error
  return products.filter(i => {
    const currentProductsNotAvailable = data
      ? data.nodes.filter(n => !n.availableForSale).map(n => n.id)
      : []

    const hasImages = i.images[0] && i.images[0].localFile

    if (noLiveData) {
      return hasImages && i.availableForSale
    }

    return hasImages && !currentProductsNotAvailable.includes(i.shopifyId)
  })
}

const ProductList = ({
  collection,
  infoCards,
  mobileCarousel,
  title,
  showTitle = true,
  featuredLayout,
  productPricePreText,
  carouselMode,
  reverseDisplayImages,
  ...rest
}) => {
  const liveProducts = useCheckProductsInStock({
    ids: collection.products.map(p => p.shopifyId),
  })

  const { mobile } = useWindowSize()
  const { products } = collection
  const firstCard =
    infoCards &&
    infoCards.find(
      i =>
        (i.position === 'first' && !i.customPosition) || i.customPosition === 1
    )

  const lastCard =
    infoCards && infoCards.find(i => i.position === 'last' && !i.customPosition)

  const productsAvailableWithImages = getAvailableProductsWithImages(
    products,
    liveProducts
  )

  const [clickable, setClickable] = useState(true)

  if (featuredLayout) {
    return (
      <Box sx={{ width: '100%', mb: [25, 25, 0] }}>
        {productsAvailableWithImages.map((i, k) => (
          <FeaturedProduct {...i} k={k} />
        ))}
      </Box>
    )
  }

  const carouselDisplay = carouselMode || false

  const dispalyReverse = reverseDisplayImages || false

  if (productsAvailableWithImages.length < 1) {
    return <Fragment />
  }

  return (
    <Box>
      {showTitle !== false && (
        <Heading
          as={mobile ? 'h5' : 'h4'}
          variant="semiBold"
          sx={{
            mb: 20,
            ml: ['1em', null, '0.5em', null, '8rem'],
            textAlign: 'left',
          }}
        >
          {title}
        </Heading>
      )}
      {carouselDisplay ? (
        <CarouselSlider
          onStartScroll={() => setClickable(false)}
          onClick={() => setClickable(true)}
          onEndScroll={() => setClickable(true)}
        >
          {productsAvailableWithImages.map((i, k) => {
            return (
              <>
                <ProductCard
                  sx={{
                    minWidth: ['65vw', null, '37vw', '37vw', '25vw'],
                    ml: k === 0 && ['1em', null, null, null, '8em'],
                    mr:
                      k === productsAvailableWithImages.length - 1
                        ? ['-.5em', null, null, null, '6.5em']
                        : '1em',
                    mb: '1em',
                  }}
                  key={k}
                  {...i}
                  carouselMode={carouselDisplay}
                  hideSwatches={true}
                  clickable={clickable}
                  reverse={dispalyReverse}
                />
                {k === productsAvailableWithImages.length - 1 && (
                  <Heading>&nbsp;</Heading>
                )}
              </>
            )
          })}
        </CarouselSlider>
      ) : (
        <Grid
          gap={[3, 4]}
          columns={[
            2,
            null,
            productsAvailableWithImages.length > 3 ? 4 : 3,
            productsAvailableWithImages.length > 3 ? 4 : 3,
          ]}
          {...rest}
          sx={{ px: '1em' }}
        >
          {firstCard && <InfoCard {...firstCard} />}
          {productsAvailableWithImages.map((i, k) => {
            const currentInfoCard =
              infoCards &&
              infoCards.find(
                i => i.customPosition === k + 1 && i.customPosition !== 1
              )

            if (currentInfoCard) {
              return [
                <InfoCard {...currentInfoCard} key={k + 'infoCard'} />,
                <ProductCard key={k} {...i} />,
              ]
            }

            return (
              <ProductCard
                key={k}
                {...i}
                productPricePreText={productPricePreText}
              />
            )
          })}
          {lastCard && <InfoCard {...lastCard} />}
        </Grid>
      )}
    </Box>
  )
}

export default ProductList
